import { Box, Tab, Tabs } from '@mui/material';
import { SocketClientMessage } from '@okletsplay/core';
import { Route } from 'app/enums';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useFetchData } from 'app/hooks/store';
import { Identifier } from 'app/interfaces';
import { LolTournamentEntity, TournamentEntity } from 'app/interfaces/entity';
import { lazyRetry } from 'app/utils';
import isEmpty from 'lodash/isEmpty';
import { Bracket } from 'modules/bracket/components';
import { MyTeam } from 'modules/tournaments/components/BracketTournament/MyTeam';
import { TournamentCheckin } from 'modules/tournaments/components/BracketTournament/TournamentCheckin';
import { TournamentInvitationList } from 'modules/tournaments/components/BracketTournament/TournamentInvitationList';
import { TournamentOverview } from 'modules/tournaments/components/BracketTournament/TournamentOverview/TournamentOverview';
import { TournamentTeams } from 'modules/tournaments/components/BracketTournament/TournamentTeams';
import { TournamentBanner } from 'modules/tournaments/components/TournamentBanner';
import { TournamentDetailsTab } from 'modules/tournaments/enums';
import { TournamentLayout } from 'modules/tournaments/layouts';
import { getTournamentById } from 'modules/tournaments/store/current-tournament/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { currentTournamentReducers } from 'modules/tournaments/store/current-tournament/slice';
import React, { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  matchPath,
  Route as ReactRoute,
  Routes as ReactRoutes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Loader } from 'shared/components/Loader';
import { GameActivityType } from 'shared/enums';
import { GameActivityPaymentProvider } from 'modules/game-payments/providers';
import { FullScreenBracketModal } from 'modules/bracket/components/FullScreenBracketModal';
import { useStyles } from './TournamentPage.styles';
import { FINISHED_TOURNAMENT_STATUSES } from '../constants';

const TournamentPrizesPage = lazyRetry(
  () => import('modules/tournaments/pages/TournamentPrizesPage')
);
const TournamentAdminPage = lazyRetry(
  () => import('modules/tournaments/pages/TournamentAdminPage')
);

const TournamentPage: FC = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const isMatch = [Route.TournamentRoot].some((path) => matchPath(path, pathname));
  const tournament = useSelector(currentTournamentSelector.tournament) as LolTournamentEntity;
  const isCurrentUserInvitedAsSpectator = useSelector(
    currentTournamentSelector.isCurrentUserInvitedAsSpectator
  );
  const isKnockOutTournament = useSelector(currentTournamentSelector.isKnockOutTournament);
  const isCurrentUserAdmin = useSelector(currentTournamentSelector.isCurrentUserAdmin);
  const currentPlayer = useSelector(currentTournamentSelector.currentPlayer);
  const isSinglePlayer = useSelector(currentTournamentSelector.isSinglePlayer);
  const actions = useMemo(() => [getTournamentById(id as string)], [id]);
  const { fetch, loading } = useFetchData<TournamentEntity>(actions, { fetchOnInit: true });

  const routeTab = useMemo(() => {
    if (isMatch) {
      const routeParts = pathname?.split('/');
      return routeParts[routeParts?.length - 1] as TournamentDetailsTab;
    }

    return TournamentDetailsTab.TournamentOverview;
  }, [isMatch, pathname]);

  const [activeTab, setActiveTab] = useState(routeTab);

  const showMyTeamTab = useMemo(() => {
    const notFinishedOrJoinedEarlier =
      !!currentPlayer || !FINISHED_TOURNAMENT_STATUSES.includes(tournament?.status);

    return !isCurrentUserInvitedAsSpectator && notFinishedOrJoinedEarlier;
  }, [isCurrentUserInvitedAsSpectator, currentPlayer, tournament?.status]);

  const onTabChangeHandler = useCallback(
    (e, tab: TournamentDetailsTab) => navigate(`${Route.Tournament}/${id as string}/${tab}`),
    [id, navigate]
  );

  const subscriptionHandler = useCallback(
    (identifier: Identifier) => {
      if (identifier !== tournament.id) {
        return;
      }

      fetch(false);
    },
    [fetch, tournament.id]
  );

  const subscriptions = useMemo(
    () => [
      {
        action: SocketClientMessage.Tournament,
        handler: subscriptionHandler,
      },
    ],
    [subscriptionHandler]
  );

  useSocketSubscriber(subscriptions);

  useEffect(() => {
    setActiveTab(routeTab);
  }, [setActiveTab, routeTab]);

  useEffect(() => {
    if (!isEmpty(tournament) && !isKnockOutTournament) {
      navigate(Route.Home);
    }
  }, [isKnockOutTournament, navigate, tournament]);

  useEffect(() => {
    return () => {
      dispatch(currentTournamentReducers.clearTournament());
    };
  }, [dispatch]);

  if (loading) {
    return <Loader />;
  }

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <TournamentBanner tournament={tournament} />
      <TournamentInvitationList />
      <TournamentCheckin />
      <Box className={classes.tabs}>
        <Tabs value={activeTab} onChange={onTabChangeHandler} variant="scrollable">
          <Tab disableRipple value={TournamentDetailsTab.TournamentOverview} label="Overview" />
          <Tab disableRipple value={TournamentDetailsTab.TournamentBracket} label="Bracket" />
          {showMyTeamTab && (
            <Tab disableRipple value={TournamentDetailsTab.TournamentMyTeam} label="My Team" />
          )}
          <Tab
            disableRipple
            value={TournamentDetailsTab.TournamentTeams}
            label={isSinglePlayer ? 'Players' : 'Teams'}
          />
          <Tab disableRipple value={TournamentDetailsTab.TournamentPrizes} label="Prizes" />
          {isCurrentUserAdmin && (
            <Tab disableRipple value={TournamentDetailsTab.TournamentAdmin} label="Admin" />
          )}
        </Tabs>
      </Box>
      <Box>
        <Suspense fallback={null}>
          <ReactRoutes>
            <ReactRoute element={<TournamentLayout />}>
              <ReactRoute path={'/overview'} element={<TournamentOverview />} />
              <ReactRoute path={'/bracket'} element={<Bracket isSubscriptionsEnabled />} />
              {showMyTeamTab && <ReactRoute path={'/my-team'} element={<MyTeam />} />}
              <ReactRoute path={'/teams'} element={<TournamentTeams />} />
              {isCurrentUserAdmin && (
                <ReactRoute path={'/admin'} element={<TournamentAdminPage />} />
              )}
              <ReactRoute path={'/prizes'} element={<TournamentPrizesPage />} />
              <ReactRoute path="*" element={<TournamentOverview />} />
            </ReactRoute>
          </ReactRoutes>
        </Suspense>
      </Box>
    </Box>
  );

  return (
    <GameActivityPaymentProvider
      gameActivity={tournament}
      activityType={GameActivityType.Tournament}
    >
      <>
        {content}
        <FullScreenBracketModal />
      </>
    </GameActivityPaymentProvider>
  );
};

export default TournamentPage;
